<script lang="ts" setup>
import UserAvatar from "@/components/UserAvatar.vue";
import ThreadList, {
  ThreadListInstance,
} from "@/components/mobile/ThreadList.vue";
import ChatMateCard from "@/components/mobile/chat/ChatMateCard.vue";
import ChatMateCardSkeleton from "@/components/mobile/chat/skeleton/ChatMateCardSkeleton.vue";
import SwipePagination from "@/components/mobile/common/swipe/SwipePagination.vue";
import { useScreenBreakpoints } from "@/composable/client";
import { mateSorterList, nsfwFilter } from "@/composable/search/useSearchQuery";
import { usePresetTags } from "@/composable/settings/tag";
import { ContentRatingEnum, MateSearchSorterEnum } from "@/enums";
import { MateSearchableFields } from "@/enums/mateEnums";
import api from "@/modules/api";
import { isInvalidDistribution } from "@/modules/common";
import { useChat } from "@/stores/chat";
import { useChatSession } from "@/stores/chatSession";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import { usePopular } from "@/stores/popular";
import { Mate } from "@/types/entity";
import { Swipe, SwipeInstance, SwipeItem } from "vant";
import {
  computed,
  nextTick,
  onActivated,
  onBeforeMount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import "vue3-carousel/dist/carousel.css";

const { t } = useI18n();
const router = useRouter();
const chatStore = useChat();
const chatSession = useChatSession();
const userStore = useGlobalUser();
const mateStore = useMate();
const popularStore = usePopular();

const mateList = ref<Mate[]>([]);
const page = ref(1);
const listRef = ref<ThreadListInstance | null>(null);
const keywordList = ref<string[]>([]);
const mode = ref<MateSearchSorterEnum>(MateSearchSorterEnum.POPULAR);
const contentRating = ref<ContentRatingEnum | undefined>(undefined);
const swipeRef = ref<SwipeInstance | null>(null);
const swipeSceneRef = ref<SwipeInstance | null>(null);
const swipeItemWidth = computed(() => 300);

const isFeaturedMateLoading = ref(false);
const isFeaturedSceneLoading = ref(false);
const isMoreLoading = ref(false);
const showFeatured = computed(() => {
  return !(isInvalidDistribution() && !userStore.user.isNsfwVisible);
});

// Lifecycle
const refreshFeatured = async (force = false) => {
  isFeaturedMateLoading.value = true;
  isFeaturedSceneLoading.value = true;
  const p2 = popularStore.getOrFetchFeaturedMateList(force).finally(() => {
    isFeaturedMateLoading.value = false;
  });
  const p1 = popularStore.getOrFetchChatScenePopularList(force).finally(() => {
    isFeaturedSceneLoading.value = false;
  });
  await Promise.all([p1, p2]);
};
const prepareData = async () => {
  isMoreLoading.value = true;
  const p2 = chatSession.getOrFetchSessionOverviewList();
  const p1 = refreshFeatured();
  const p3 = loadMore().finally(() => {
    isMoreLoading.value = false;
  });
  await Promise.all([p1, p2, p3]);
};

onBeforeMount(async () => {
  prepareData();
});

onMounted(async () => {
  await nextTick();
  listRef.value?.scrollTo(chatStore.exploreScrollY);
});
onActivated(async () => {
  await nextTick();
  listRef.value?.scrollTo(chatStore.exploreScrollY);
});

const { tablet } = useScreenBreakpoints();
// Event Handler
const loadMore = async () => {
  const perPage = tablet ? 30 : 9;
  const resp = await api.readMateSearchPage({
    pagination: { perPage: perPage, page: page.value },
    filter: {
      query: keywordList.value.join(" "),
      searchFields:
        keywordList.value.length > 0 ? [MateSearchableFields.TAG] : undefined,
      isNsfw: contentRating.value,
      isProfileSet: 1,
    },
    tab: mode.value,
  });
  mateList.value = [...mateList.value, ...resp.page.data] as Mate[];
  mateStore.updateMateMap(resp.page.data, false);
  if (resp.page.data.length < perPage) {
    return false;
  } else {
    page.value++;
    return true;
  }
};
const { presetTags } = usePresetTags();
const showAllTags = ref(false);

watch([mode, keywordList, contentRating], async () => {
  page.value = 1;
  mateList.value = [];
  await nextTick();
  listRef.value?.resetScrollLoadingState();
});
</script>
<template>
  <div class="h-full w-full grid grid-rows-[auto_1fr] overflow-hidden">
    <thread-list
      ref="listRef"
      :immediate-check="false"
      :on-scroll-load="() => loadMore()"
      :onPullLoad="() => refreshFeatured(true)"
      class="px-2"
      enable-back-top
      @onScroll="chatStore.exploreScrollY = $event"
    >
      <div
        v-if="userStore.isLogin"
        class="flex flex-col gap-2 w-full overflow-hidden pb-4"
      >
        <h1 class="text-2xl font-bold text-base-content px-2">
          {{ $t("Buttons.recentChat") }}
        </h1>
        <div class="w-full overflow-y-auto no-scrollbar lg:show-scroller">
          <div class="gap-2 py-1 shrink-0 flex">
            <div
              class="w-[86px] h-[136px] shrink-0 flex flex-col justify-center gap-4 items-center cursor-pointer bg-neutral/80 hover:bg-neutral p-1 rounded-lg overflow-hidden"
              @click="mateStore.clickCreateMate()"
            >
              <i
                class="iconify mingcute--add-circle-fill text-primary text-xl"
              />
              <div class="text-center">
                {{ $t("Buttons.createNewCharacter") }}
              </div>
            </div>
            <div
              v-for="item in chatSession.sessionOverviewList"
              :key="item.id"
              class="w-[86px] h-[136px] gap-1 rounded-lg overflow-hidden shrink-0 flex flex-col shadow-lg cursor-pointer bg-neutral/80 hover:bg-neutral p-1"
              @click="chatSession.goChat(router, item.uuid, item.id)"
            >
              <user-avatar
                :avatar-url="item.avatarUrl"
                class="w-full shrink-0 rounded-lg"
              />
              <div class="line-clamp-1 text-center text-xs lg:text-sm">
                {{ item.title }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showFeatured" class="mb-2 flex flex-col gap-2">
        <h1 class="text-2xl font-bold text-base-content">
          {{ t(`Message.featuredCharacter`) }}
        </h1>
        <div class="flex flex-col gap-1">
          <div
            v-if="isFeaturedMateLoading"
            class="w-full overflow-x-hidden flex gap-1"
          >
            <ChatBannerCardSekeltone
              v-for="i in 5"
              :key="i"
              :style="{
                width: swipeItemWidth + 'px',
              }"
              class="shrink-0"
            />
          </div>
          <Swipe v-else ref="swipeRef" :loop="false" :width="swipeItemWidth">
            <SwipeItem
              v-for="item in popularStore.featuredMateList"
              :key="item.id"
              class="box-border pr-2"
            >
              <ChatBannerCard
                :mate="item"
                @click="
                  router.push({
                    name: 'mateChatIntro',
                    params: { name: item.name, uuid: item.uuid },
                  })
                "
              />
            </SwipeItem>
            <template #indicator="{ active, total }">
              <SwipePagination
                :active="active"
                :total="total"
                @next="swipeRef?.next()"
                @prev="swipeRef?.prev()"
              />
            </template>
          </Swipe>
        </div>
      </div>
      <div v-if="showFeatured" class="mb-2 flex flex-col gap-2">
        <h1 class="text-2xl font-bold text-base-content">
          {{ t(`Message.featuredScenario`) }}
        </h1>
        <div class="flex flex-col">
          <div
            v-if="isFeaturedSceneLoading"
            class="w-full overflow-x-hidden flex gap-1"
          >
            <ChatSceneStateCardSkeleton
              v-for="i in 5"
              :key="i"
              class="shrink-0 w-[300px] items-stretch h-[300px]"
            />
          </div>
          <Swipe
            v-else
            ref="swipeSceneRef"
            :loop="false"
            :width="swipeItemWidth"
          >
            <SwipeItem
              v-for="scene in popularStore.featuredChatSceneList"
              :key="scene.id"
              class="box-border pr-2"
            >
              <ChatSceneStateCard
                :scene="scene"
                class="shrink-0 w-full items-stretch h-[300px]"
              />
            </SwipeItem>
            <template #indicator="{ active, total }">
              <SwipePagination
                :active="active"
                :total="total"
                @next="swipeSceneRef?.next()"
                @prev="swipeSceneRef?.prev()"
              />
            </template>
          </Swipe>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-2">
        <h1 class="text-2xl font-bold text-base-content">
          {{ t(`Message.moreCharacters`) }}
        </h1>
        <div class="flex gap-1">
          <SingleSelect v-model:selected="mode" :options="mateSorterList" />
          <SingleSelect
            v-if="!userStore.isHideNSFW"
            v-model:selected="contentRating"
            :options="nsfwFilter"
          />
        </div>
        <div class="grid grid-cols-[1fr_auto] items-center gap-2">
          <div
            :class="{
              'flex-wrap': showAllTags,
            }"
            class="flex w-full overflow-hidden gap-1 items-center"
          >
            <SelectableTagList
              v-model:selected="keywordList"
              :enable-multi-select="false"
              :list="presetTags"
              :wrap="showAllTags"
              enable-all
            />
            <button
              v-if="showAllTags"
              class="btn btn-xs btn-primary rounded-full"
              @click="showAllTags = showAllTags = false"
            >
              {{ $t("Buttons.collapse") }}
            </button>
          </div>
          <button
            v-if="!showAllTags"
            class="btn btn-xs btn-primary rounded-full"
            @click="showAllTags = showAllTags = true"
          >
            {{ $t("Buttons.allTags") }}
          </button>
        </div>
      </div>
      <div>
        <div
          class="grid grid-cols-3 sm:grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-1 xl:gap-3 items-stretch"
        >
          <template v-if="isMoreLoading">
            <ChatMateCardSkeleton v-for="i in 6" :key="i" />
          </template>
          <template v-else>
            <ChatMateCard
              v-for="item in mateList"
              :key="item.uuid"
              :mate="item"
              :show-count="false"
            />
          </template>
        </div>
      </div>
    </thread-list>
  </div>
</template>
